/**
 * GEN-3167 — para onde voltar depois do login.
 *
 * Até aqui o login mandava para `/` sempre. Quem recebia um link de uma tela específica e não
 * estava com a sessão aberta perdia o destino: fazia login e caía no Dashboard, tendo que
 * reencontrar sozinho o que o link já apontava. Isso vale para QUALQUER link do produto — a rota
 * deep-linkável da atividade (Kanban 2.0) só tornou o buraco visível, porque é o primeiro caso em
 * que o link é o meio de trabalho (CTA de e-mail, link mandado a um colega).
 *
 * `sessionStorage` de propósito: o destino é da ABA e da tentativa de login corrente — não deve
 * sobreviver ao fechamento nem vazar para outra aba. Também é o único armazenamento que atravessa
 * o `localStorage.clear()` que o LoginForm executa ao gravar a sessão nova.
 *
 * ⚠️ SEGURANÇA (open redirect): o destino vem da barra de endereços, ou seja, é entrada do
 * usuário — e um atacante pode montar um link que leve o usuário a fazer login e ser jogado num
 * site externo com aparência de "continuação" do fluxo. Por isso `isSafeInternalPath` só aceita
 * caminho relativo à raiz deste domínio: exatamente uma `/` inicial, sem esquema e sem `//` ou
 * `\\` (que o navegador resolve como URL protocol-relative para OUTRO host).
 */

const KEY = "taxly:returnTo";

/** Telas de autenticação: guardar uma delas faria o login redirecionar para si mesmo. */
const ROTAS_PUBLICAS = ["/login", "/logout", "/private-signup", "/complete-registration"];

export function isSafeInternalPath(path: unknown): path is string {
  if (typeof path !== "string" || path.length === 0 || path.length > 2048) return false;
  if (!path.startsWith("/")) return false; // absoluto externo, relativo, "javascript:", mailto:…
  if (path.startsWith("//") || path.startsWith("/\\")) return false; // protocol-relative → outro host
  if (/[\x00-\x1F]/.test(path)) return false; // caractere de controle
  // deep-review: comparar em minúsculas. Nenhuma rota do app é `caseSensitive`, então `/LOGIN` é a
  // mesma tela que `/login` — uma denylist sensível à caixa protegeria só a grafia que ela conhece.
  const semQuery = path.split(/[?#]/)[0].toLowerCase();
  return !ROTAS_PUBLICAS.some((r) => semQuery === r || semQuery.startsWith(`${r}/`));
}

/** Guarda o destino pretendido antes de mandar o usuário para o login. */
export function rememberReturnTo(path: string): void {
  if (!isSafeInternalPath(path)) return;
  try {
    sessionStorage.setItem(KEY, path);
  } catch {
    /* storage indisponível — o login segue com o comportamento antigo (vai para "/") */
  }
}

/**
 * Lê o destino e o CONSOME (uma tentativa de login = um retorno). Sem isto, um login posterior
 * no mesmo navegador seria sequestrado por um destino velho.
 */
export function takeReturnTo(): string | null {
  try {
    const v = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    return isSafeInternalPath(v) ? v : null;
  } catch {
    return null;
  }
}

/**
 * Descarta o destino sem usá-lo. Obrigatório no LOGOUT: quem sai de propósito não deve, no login
 * seguinte, ser levado à tela onde a sessão anterior morreu — inclusive porque pode ser outra pessoa
 * entrando no mesmo navegador.
 */
export function clearReturnTo(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* noop */
  }
}

/** Destino guardado, ou a home. É o que os fluxos de login passam para o `navigate`. */
export function destinoAposLogin(): string {
  return takeReturnTo() ?? "/";
}
